import React, { useMemo } from 'react';
import Plot from 'react-plotly.js';

// =====================================================================
// Types
// =====================================================================

interface DataPoint {
  concentration: number;
  concentration_unit: string;
  response_value: number;
  response_unit: string;
  replicate_number: number;
}

interface Statistics {
  stat_id: number;
  assay_name: string;
  measurement_type: string;
  n_replicates: number | null;
  mean_value: number | null;
  std_deviation: number | null;
  ci_low: number | null;
  ci_high: number | null;
  curve_fit_r2: number | null;
  hill_slope: number | null;
}

interface DoseResponseChartProps {
  dataPoints: DataPoint[];
  statistics: Statistics;
  batchCode: string;
  assayName: string;
}

// =====================================================================
// 4PL curve fitting
// =====================================================================

function fourPL(x: number, bottom: number, top: number, ic50: number, hill: number): number {
  return bottom + (top - bottom) / (1 + Math.pow(x / ic50, hill));
}

function fit4PL(concentrations: number[], means: number[]): { bottom: number; top: number; ic50: number; hill: number } | null {
  if (concentrations.length < 3) return null;

  const sortedPairs = concentrations.map((c, i) => ({ c, m: means[i] })).sort((a, b) => a.c - b.c);
  const sortedC = sortedPairs.map(p => p.c);
  const sortedM = sortedPairs.map(p => p.m);

  // Initial estimates
  let bottom = Math.min(...sortedM);
  let top = Math.max(...sortedM);
  let ic50 = Math.sqrt(sortedC[0] * sortedC[sortedC.length - 1]) || sortedC[Math.floor(sortedC.length / 2)];
  let hill = -1;

  // Simple iterative optimization (gradient-free Nelder-Mead-style)
  const residual = (b: number, t: number, i: number, h: number) => {
    let ss = 0;
    for (let k = 0; k < sortedC.length; k++) {
      const pred = fourPL(sortedC[k], b, t, i, h);
      ss += (sortedM[k] - pred) ** 2;
    }
    return ss;
  };

  // Grid search for better starting IC50
  let bestSS = residual(bottom, top, ic50, hill);
  const logMin = Math.log10(Math.max(sortedC[0], 0.001));
  const logMax = Math.log10(sortedC[sortedC.length - 1]);
  for (let li = logMin; li <= logMax; li += (logMax - logMin) / 20) {
    const tryIC50 = Math.pow(10, li);
    for (const tryHill of [-0.5, -1, -1.5, -2, -3]) {
      const ss = residual(bottom, top, tryIC50, tryHill);
      if (ss < bestSS) {
        bestSS = ss;
        ic50 = tryIC50;
        hill = tryHill;
      }
    }
  }

  // Iterative refinement
  const params = [bottom, top, ic50, hill];
  const steps = [
    (top - bottom) * 0.1,
    (top - bottom) * 0.1,
    ic50 * 0.3,
    0.3,
  ];

  for (let iter = 0; iter < 200; iter++) {
    for (let p = 0; p < 4; p++) {
      const current = residual(params[0], params[1], params[2], params[3]);
      // Try +step
      params[p] += steps[p];
      if (params[2] <= 0) params[2] = 0.001; // IC50 must be positive
      const plusSS = residual(params[0], params[1], params[2], params[3]);
      if (plusSS < current) continue; // Keep improvement
      // Revert and try -step
      params[p] -= 2 * steps[p];
      if (params[2] <= 0) params[2] = 0.001;
      const minusSS = residual(params[0], params[1], params[2], params[3]);
      if (minusSS < current) continue; // Keep improvement
      // Revert — no improvement
      params[p] += steps[p];
    }
    // Shrink steps
    for (let p = 0; p < 4; p++) steps[p] *= 0.95;
  }

  return { bottom: params[0], top: params[1], ic50: params[2], hill: params[3] };
}

// =====================================================================
// Component
// =====================================================================

const DoseResponseChart: React.FC<DoseResponseChartProps> = ({ dataPoints, statistics, batchCode, assayName }) => {
  const chartData = useMemo(() => {
    if (!dataPoints || dataPoints.length === 0) return null;

    // Group by concentration to compute mean ± SD
    const grouped: Record<number, number[]> = {};
    dataPoints.forEach(dp => {
      const c = dp.concentration;
      if (!grouped[c]) grouped[c] = [];
      grouped[c].push(dp.response_value);
    });

    const concentrations = Object.keys(grouped).map(Number).sort((a, b) => a - b);
    const means: number[] = [];
    const sds: number[] = [];

    concentrations.forEach(c => {
      const vals = grouped[c];
      const mean = vals.reduce((s, v) => s + v, 0) / vals.length;
      means.push(mean);
      if (vals.length > 1) {
        const variance = vals.reduce((s, v) => s + (v - mean) ** 2, 0) / (vals.length - 1);
        sds.push(Math.sqrt(variance));
      } else {
        sds.push(0);
      }
    });

    // Fit 4PL curve
    const ic50FromStats = statistics.mean_value;
    const hillFromStats = statistics.hill_slope;
    let fitParams = fit4PL(concentrations, means);

    // If we have stats IC50, use it to improve/override
    if (ic50FromStats && fitParams) {
      fitParams.ic50 = ic50FromStats;
      if (hillFromStats) fitParams.hill = hillFromStats;
    } else if (ic50FromStats && !fitParams) {
      // Fallback: create params from stats
      fitParams = {
        bottom: Math.min(...means),
        top: Math.max(...means),
        ic50: ic50FromStats,
        hill: hillFromStats || -1,
      };
    }

    // Generate fitted curve points (smooth)
    let curveX: number[] = [];
    let curveY: number[] = [];
    if (fitParams && concentrations.length >= 2) {
      const logMin = Math.log10(Math.max(concentrations[0] * 0.3, 0.001));
      const logMax = Math.log10(concentrations[concentrations.length - 1] * 3);
      const nPoints = 100;
      for (let i = 0; i <= nPoints; i++) {
        const logX = logMin + (logMax - logMin) * (i / nPoints);
        const x = Math.pow(10, logX);
        curveX.push(x);
        curveY.push(fourPL(x, fitParams.bottom, fitParams.top, fitParams.ic50, fitParams.hill));
      }
    }

    return {
      concentrations,
      means,
      sds,
      fitParams,
      curveX,
      curveY,
      allPoints: dataPoints,
    };
  }, [dataPoints, statistics]);

  if (!chartData) return null;

  const { concentrations, means, sds, fitParams, curveX, curveY, allPoints } = chartData;

  const ic50Value = fitParams?.ic50 ?? statistics.mean_value;
  const responseAt50 = fitParams ? fourPL(fitParams.ic50, fitParams.bottom, fitParams.top, fitParams.ic50, fitParams.hill) : 50;

  // --- Traces ---

  // Individual data points (scatter)
  const individualTrace: Plotly.Data = {
    x: allPoints.map(dp => dp.concentration),
    y: allPoints.map(dp => dp.response_value),
    mode: 'markers' as const,
    type: 'scatter' as const,
    name: 'Replicates',
    marker: {
      color: 'rgba(96, 165, 250, 0.5)',
      size: 6,
      symbol: 'circle',
      line: { color: 'rgba(96, 165, 250, 0.8)', width: 1 },
    },
    hovertemplate: 'Conc: %{x} nM<br>Response: %{y:.1f}%<br>Replicate<extra></extra>',
    showlegend: false,
  };

  // Mean points with error bars
  const meanTrace: Plotly.Data = {
    x: concentrations,
    y: means,
    mode: 'markers' as const,
    type: 'scatter' as const,
    name: 'Mean',
    marker: {
      color: '#0d9488',
      size: 9,
      symbol: 'circle',
      line: { color: '#14b8a6', width: 1.5 },
    },
    error_y: {
      type: 'data' as const,
      array: sds,
      visible: true,
      color: '#0d9488',
      thickness: 1.5,
      width: 3,
    },
    hovertemplate: concentrations.map((c, i) =>
      `Conc: ${c} nM<br>Mean: ${means[i].toFixed(1)}%<br>SD: ±${sds[i].toFixed(1)}<extra></extra>`
    ),
    showlegend: false,
  };

  // Fitted 4PL curve
  const curveTrace: Plotly.Data = {
    x: curveX,
    y: curveY,
    mode: 'lines' as const,
    type: 'scatter' as const,
    name: '4PL Fit',
    line: { color: '#14b8a6', width: 2, shape: 'spline' as const },
    hoverinfo: 'skip' as const,
    showlegend: false,
  };

  const traces: Plotly.Data[] = [individualTrace, meanTrace];
  if (curveX.length > 0) traces.push(curveTrace);

  // --- Annotations & shapes ---
  const shapes: Partial<Plotly.Shape>[] = [];
  const annotations: Partial<Plotly.Annotations>[] = [];

  // 50% response horizontal dashed line
  const xMin = concentrations.length > 0 ? concentrations[0] * 0.3 : 0.1;
  const xMax = concentrations.length > 0 ? concentrations[concentrations.length - 1] * 3 : 10000;

  shapes.push({
    type: 'line',
    x0: xMin,
    x1: xMax,
    y0: 50,
    y1: 50,
    xref: 'x',
    yref: 'y',
    line: { color: 'rgba(156, 163, 175, 0.3)', width: 1, dash: 'dash' },
  });

  // IC50 vertical dashed line + annotation
  if (ic50Value && ic50Value > 0) {
    shapes.push({
      type: 'line',
      x0: ic50Value,
      x1: ic50Value,
      y0: 0,
      y1: responseAt50,
      xref: 'x',
      yref: 'y',
      line: { color: 'rgba(245, 158, 11, 0.5)', width: 1, dash: 'dash' },
    });

    const formatIC50 = ic50Value >= 1000 ? `${(ic50Value / 1000).toFixed(1)} µM` : `${ic50Value.toFixed(1)} nM`;
    annotations.push({
      x: Math.log10(ic50Value),
      y: responseAt50 + 5,
      xref: 'x',
      yref: 'y',
      text: `${statistics.measurement_type || 'IC50'} = ${formatIC50}`,
      showarrow: false,
      font: { color: '#f59e0b', size: 10, family: 'monospace' },
      bgcolor: 'rgba(15, 23, 42, 0.8)',
      borderpad: 3,
    });
  }

  // --- Layout ---
  const layout: Partial<Plotly.Layout> = {
    height: 250,
    margin: { l: 45, r: 15, t: 10, b: 40 },
    paper_bgcolor: 'transparent',
    plot_bgcolor: 'transparent',
    xaxis: {
      type: 'log',
      title: { text: 'Concentration (nM)', font: { color: '#9ca3af', size: 11 }, standoff: 5 },
      tickfont: { color: '#9ca3af', size: 10, family: 'monospace' },
      gridcolor: '#1e293b',
      gridwidth: 1,
      zeroline: false,
      showline: true,
      linecolor: '#334155',
      linewidth: 1,
      range: [Math.log10(xMin), Math.log10(xMax)],
    },
    yaxis: {
      title: { text: 'Response (%)', font: { color: '#9ca3af', size: 11 }, standoff: 5 },
      tickfont: { color: '#9ca3af', size: 10, family: 'monospace' },
      gridcolor: '#1e293b',
      gridwidth: 1,
      zeroline: false,
      showline: true,
      linecolor: '#334155',
      linewidth: 1,
    },
    shapes,
    annotations,
    hovermode: 'closest',
    dragmode: false,
  };

  const config: Partial<Plotly.Config> = {
    displayModeBar: false,
    responsive: true,
    staticPlot: false,
  };

  return (
    <div style={{ width: '100%', marginTop: 6 }}>
      <Plot
        data={traces}
        layout={layout}
        config={config}
        style={{ width: '100%', height: 250 }}
        useResizeHandler
      />
    </div>
  );
};

export default DoseResponseChart;
